import {
    AddType, BlurEvent, Button, ChangeEvent, ClickEvent, Component, DataDisplayEvent, DataSource,
    DataSourceMode, EditRowDecorator, Image, Label, LabelProps, Layout, LookupModelSearchEvent, Overlay,
    Panel, TableRow, TableRowDisplayEvent, TableRowMode, Textbox, DropdownSelectionEvent
} from "@mcleod/components";
import { Alignment, Api, Currency, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { CarrierOfferCounter, CounterOfferSummaryInfo, MovementCarrierPay, OfferField } from "@mcleod/powerbroker";
import { QuickOfferSlideout } from "./QuickOfferSlideout";
import { OfferTabEvent, OfferTabEventAction, OfferTabType } from "./MovementOfferTabs";
import { OfferSource, OfferUtil } from "./OfferUtil";
import { isSpotOrder } from "./Orders";
import { QuickSellDetails } from "./QuickSellDetails";
import { AutogenLayoutMovementOffers } from "./autogen/AutogenLayoutMovementOffers";
import { ModelMovement } from "./models/ModelMovement";
import { DispatchUtil } from "./DispatchUtil";
import { CommonTooltips } from "@mcleod/common";
import { Movement } from "./Movement";
import { LMEGeneralSettings } from "@mcleod/general";
import {InvitedCarrierWithPrequalQuickInfo} from "@mcleod/powerbroker/src/InvitedCarrierWithPrequalQuickInfo";


const log = LogManager.getLogger("lme.dispatch.MovementOffers");

export class MovementOffers extends AutogenLayoutMovementOffers {
    private orderID: string;
    private moveRow: ModelRow;
    private movementID: string;
    private offerUtil: OfferUtil;
    stopCountText: string;
    public onTabEvent: (offerTabEvent: OfferTabEvent) => void = () => { };
    private static OFFER_STATUS_OPTIONS: any[] = [
        { displayValue: "Initial Offer", value: "I" },
        { displayValue: "Pending Accept", value: "Z" },
        { displayValue: "Countered", value: "O" },
        { displayValue: "Electronic Offer", value: "F" },
        { displayValue: "Declined", value: "D" }
    ];
    public mainLayout: Movement;

    private sendTabEvent(offerTabEvent: OfferTabEvent) {
        this.onTabEvent(offerTabEvent);
    }

    public initializeOfferUtil(movementID: string, moveRow: ModelRow) {
        this.offerUtil = new OfferUtil(movementID, moveRow, this, this.mainLayout);
        if (this.tableOffers.quickAddRow?.populatedDOM) { //Redisplay quick add row to trigger Offer Util row display logic
            this.tableOffers.resetQuickAddRow();
        }
    }

    override onLoad() {
        this.tableOffers.onEditLayoutLoaded = this._offerLayoutLoaded();
    }

    async initialize(orderID: string, movementSource: DataSource, movementID: string) {
        this.orderID = orderID;
        this.sourceMovement = movementSource;
        this.movementID = movementID;
        await this.setMovementRow();
        this.initializeOfferUtil(movementID, this.moveRow);
        await this.search();
        this.shouldAllowQuickAdd();
        this.mainDataSource.mode = DataSourceMode.UPDATE;
    }

    public shouldAllowQuickAdd() {
        if (this.moveRow.getString("manifest_hold") == "Y") {
            const msg = "Cannot add offers while the manifest is on hold.";
            this.tableOffers.addType = AddType.NOT_ALLOWED;
            this.tableOffers.tooltip = msg;
            this.tableOffers.emptyCaptionAfterSearch = msg;
        }
    }

    public async setMovementRow() {
        if (this.sourceMovement === null) {
            this.moveRow = await new ModelMovement().searchSingle({ id: this.movementID }, "lme/dispatch/Movement");
        } else {
            this.moveRow = this.sourceMovement.activeRow;
        }
    }

    public async search() {
        const movementId = this.moveRow?.get("id");
        if (movementId) {
            await this.sourceCarrierOffer.search({ "movement_id": this.moveRow?.get("id") });
            this.sendTabEvent({ type: OfferTabType.PENDING, action: OfferTabEventAction.SEARCH });
        }
    }

    private _offerLayoutLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const layout = rowDecorator.layout as QuickOfferSlideout
            rowDecorator.doAfterSlideIn = () => {
                if (this.mainDataSource.activeRow?.get("number_type") != null) {
                    switch (this.mainDataSource.activeRow.get("number_type")) {
                        case QuickOfferSlideout.MC_NUMBER:
                            layout.displayPrequalResult("MC", this.mainDataSource.activeRow.get("number"));
                            break;
                        case QuickOfferSlideout.DOT:
                            layout.displayPrequalResult("DOT", this.mainDataSource.activeRow.get("number"));
                            break;
                    }
                }
                else {
                    if(!StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("icc_number"))){
                        layout.displayPrequalResult("MC", this.mainDataSource.activeRow.get("icc_number"));
                    }else{
                        layout.displayPrequalResult("DOT", this.mainDataSource.activeRow?.get("dot_number"));
                    }
                }
            }
            rowDecorator.labelTitle.text = `Quick Offer - Order ${this.orderID}`;
            rowDecorator.onSave = (offerRow: ModelRow) => {
                const tbIccNum = tableRow.findComponentById("textboxIccNumber") as Textbox;
                //For some reason this field is not being updated with the amount.amount value so this is the reason for the setter
                offerRow.set("amount_n", offerRow.getCurrency("amount", null)?.amount);
                if (tbIccNum) {
                    this.offerUtil.iccNumberRequired(tbIccNum, offerRow);
                }
                tableRow.saveChangesFromEdit(offerRow);
                tableRow.data.setLookupModelData("carrier_name", new ModelRow(layout.tbContactName.lookupModel, false, { "name": tableRow.data.get("carrier_name") })); // TODO: temp solution for lookup model issue
                tableRow.data.setLookupModelData("contact_name", new ModelRow(layout.tbContactName.lookupModel, false, { "name": tableRow.data.get("contact_name") })); // TODO: temp solution for lookup model issue
                this.displayStatus(tableRow);
            };

            layout.assignableCheck = () => DispatchUtil.isAssignable(this.moveRow, { btnAssign: layout.buttonAssignCarrier });
            const quickSellDetails = layout.layoutQuickSellDetails as QuickSellDetails;
            quickSellDetails.configureForSlideout(this.stopCountText);
            quickSellDetails.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [this.moveRow]);
            const loCarrierPay = layout.loCarrierPay as MovementCarrierPay;
            log.debug("Offer data:", layout.mainDataSource.activeRow)
            const parentRow = this.mainDataSource.parentDataSource?.activeRow;
            this.setNumberByType(layout.mainDataSource.activeRow);
            if (!parentRow?.isEmpty() && parentRow instanceof ModelMovement) {
                loCarrierPay.mainDataSource.data[0] = this.mainDataSource.parentDataSource.activeRow;
                loCarrierPay.mainDataSource.rowIndex = 0;
                this._loadSlideoutData(layout, loCarrierPay);
            } else {
                new ModelMovement().searchSingle({ id: this.moveRow.get("id") }, "lme/powerbroker/CarrierAssignment").then(moveRow => {
                    loCarrierPay.mainDataSource.data.push(moveRow);
                    loCarrierPay.mainDataSource.rowIndex = 0;
                    this._loadSlideoutData(layout, loCarrierPay)
                });
            }

            layout.mainDataSource.displayDataInBoundComponents();
            layout.assignButtonClicked = () => rowDecorator.multiButton.primaryButton.clicked(); // triggers the save and close
            layout.carrierAssigned = async () => {
                await this.offerUtil.doAfterOfferAssignment();
                this.mainLayout?.mainDataSource.search({ id: this.movementID });
            }
            loCarrierPay.buttonOverrideTargetPay.enabled = false;
            loCarrierPay.buttonOverrideMaxPay.enabled = false;
        }
    }

    private async _loadSlideoutData(layout: QuickOfferSlideout, loCarrierPay: MovementCarrierPay): Promise<void> {
        const offerRow = layout.mainDataSource.activeRow;
        if (!offerRow.isNull("payee_id") && offerRow.isNull("carrier_rate_id")) {
            await this.calculateCarrierRate(offerRow);
        }
        const amount = offerRow.getCurrency("amount");
        const carrierRate = offerRow.getCurrency("carrier_rate");
        if (amount && (amount?.getAmount() == carrierRate?.getAmount())) {
            layout.labelAutoRated.visible = true;
            CommonTooltips.setTooltipContentLayout(
                layout.labelAutoRated,
                offerRow.get("carrier_rate_lane_id", null),
                "lme/dispatch/rates/CarrierLaneRateQuickInfo"
            );
        } else {
            layout.labelAutoRated.visible = false;
        }
        layout.displayRatingStar(offerRow);
        const carrierName = offerRow?.get("carrier_name");
        const contactName = offerRow?.get("contact_name");
        const tractorCityId = offerRow?.get("tractor_city_id");
        offerRow.set("order_id", this.orderID);
        offerRow.set("orders_on_hold", this.moveRow.get("orders.on_hold", null));
        offerRow.set("pending_payee", this.moveRow.get("pending_payee_id", null) ?? this.moveRow.get("has_pending_assignment", false));
        offerRow.set("is_subject_order", isSpotOrder(this.moveRow.get("orders.subject_order_status", null)));
        offerRow.set("order_total_chg", loCarrierPay.mainDataSource.activeRow.get("orders.total_charge"));
        loCarrierPay.layoutTargetPay.setupMpact(loCarrierPay.mainDataSource.activeRow);
        loCarrierPay.displayCalculatedFields().then(() => {
            layout.updateMargins(OfferField.OFFER, "offer_margin", layout.tbDispatchMargin, layout.tbDispatchMarginPrcnt, true);
            layout.updateMargins(OfferField.COUNTER, "counter_margin", layout.tbCounterMargin, layout.tbCounterMarginPrcnt, true);
            if (layout.panelBuyToMarket.visible) {
                layout.updateBuyToMarket();
            }
        });
        offerRow.set("movement_id", loCarrierPay.mainDataSource.activeRow?.get("id"));

        if (carrierName) {
            offerRow.setLookupModelData("carrier_name", new ModelRow(layout.tbCarrierName.lookupModel, false, { "name": carrierName }));
            layout.tbCarrierName.displayData(offerRow, null, 0);
        }
        if (contactName) offerRow.setLookupModelData("contact_name", new ModelRow(layout.tbContactName.lookupModel, false, { "name": contactName }));
        if (tractorCityId && loCarrierPay.mainDataSource.activeRow?.get("id") != null) {
            Api.post("lme/dispatch/get-next-stop-distance", {
                city_id: tractorCityId,
                movement_id: loCarrierPay.mainDataSource.activeRow?.get("id")
            }).then(response => {
                offerRow.set("miles_to_origin", response?.data[0]?.distance);
                layout.tbMilesToPickup.text = response?.data[0]?.distance;
            })
        }
    }

    setNumberByType(offerRow: ModelRow) {
        switch (offerRow.get("number_type")) {
            case QuickOfferSlideout.DOT:
                offerRow.set("dot_number", offerRow.get("number"));
                break;
            case QuickOfferSlideout.INTRASTATE:
                offerRow.set("intrastate_code", offerRow.get("number"));
                break;
            case QuickOfferSlideout.MC_NUMBER:
                offerRow.set("icc_number", offerRow.get("number"));
                break;
        }
    }

    public fillOfferReferenceData(modelRow: ModelRow, movementRow: ModelRow, orderId: string, movementId: string) {
        modelRow.set("order_id", orderId);
        modelRow.set("movement_id", movementId);
        if (movementRow.get("override_targetpay") != null)
            modelRow.set("target_pay", movementRow.get("override_targetpay"));
        else
            modelRow.set("target_pay", movementRow.get("target_pay"));
        if (movementRow.get("override_max_pay") != null)
            modelRow.set("max_pay", movementRow.get("override_max_pay"));
        else
            modelRow.set("max_pay", movementRow.get("max_buy"));
        modelRow.set("order_total_chg", movementRow.get("orders.total_charge"));
    }

    /** This is an event handler for the onBlur event of textboxIccNumber.  */
    textboxIccNumberOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox = event.target as Textbox;
            const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
            this.populateTableRowFromIccOrDotNumber(tableRow);
        }
    }

    textboxIccNumberOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
        const textboxCarrierName = tableRow.findComponentById("textboxCarrierName") as Textbox;
        const textboxContactName = tableRow.findComponentById("textboxContactName") as Textbox;
        const textboxContactPhone = tableRow.findComponentById("textboxContactPhone") as Textbox;
        const textboxContactEmail = tableRow.findComponentById("textboxContactEmail") as Textbox;
        // clear textbox fields if ICC number is changed
        if (event.oldValue != event.newValue && event.userInitiatedChange) {
            const modelRow: ModelRow = tableRow.data;
            textboxContactName.text = "";
            textboxContactPhone.text = "";
            textboxContactEmail.text = "";
            textboxCarrierName.text = "";
            modelRow.set("carrier_name", null);
            modelRow.set("contact_name", null);
            modelRow.set("contact_phone", null);
            modelRow.set("email", null);
        }
        this.setWarningLabels(tableRow, false);
    }

    textboxAmountOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
        if (event.oldValue != event.newValue && event.userInitiatedChange) {
            tableRow.data.set("carrier_rate_id", null);
            tableRow.data.set("carrier_rate_lane_id", null);
            this.offerUtil.setTooltipContent(tableRow);
        }
    }

    async populateTableRowFromIccOrDotNumber(tableRow: TableRow) {
        const modelRow: ModelRow = tableRow.data;
        const prequalWarningLabel: Label = tableRow.findComponentById("imageWarning") as Label;
        if (prequalWarningLabel) {
            prequalWarningLabel.visible = false;
        }

        const number: String = modelRow.get("number");
        if (StringUtil.isEmptyString(number)) {
            this.setCarrierData(tableRow, null);
            this.setWarningLabels(tableRow, false);
            return;
        }

        let icc_number: string , dot_number: string, intrastate_code: string  = null;
        switch (modelRow.get("number_type")) {
            case QuickOfferSlideout.DOT:
                dot_number = modelRow.get("number");
                break;
            case QuickOfferSlideout.INTRASTATE:
                intrastate_code = modelRow.get("number");
                break;
            case QuickOfferSlideout.MC_NUMBER:
                icc_number = modelRow.get("number");
                break;
        }
        const response = await Api.search("lme/dispatch/carrier", icc_number ? { icc_number } : dot_number ? { dot_number } : { intrastate_code });
        const carrierData = response?.data?.[0];
        if (carrierData != null) {
            this.setCarrierData(tableRow, carrierData);
            this.offerUtil.validateCarrierForDispatch(tableRow);
            await this.calculateCarrierRate(modelRow);
            tableRow.displayComponentValues();
            if(!StringUtil.equalsIgnoreCase(carrierData.onboarded,"Y")){
                if (await this.validActiveVendor()) {
                    if (!StringUtil.isEmptyString(icc_number) || !StringUtil.isEmptyString(dot_number)) {
                        this.prequalifyCarrier(icc_number, dot_number, modelRow.get("number_type"), tableRow);
                    }
                }
            }
        } else if (await this.validActiveVendor()) {
            if(!StringUtil.isEmptyString(icc_number) || !StringUtil.isEmptyString(dot_number)) {
                this.prequalifyCarrier(icc_number, dot_number, modelRow.get("number_type"), tableRow);
            }
        }
    }

    async calculateCarrierRate(modelRow: ModelRow) {
        const response = await Api.search("lme/dispatch/calculate-override-pay", {
            "movement_id": this.moveRow.get("id"),
            "order_id": this.moveRow.get("orders.id"),
            "payee_id": modelRow.get("payee_id"),
            "auto_rate": LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y",
            "calculate_fuel_surcharge": true
        });
        if (response.data[0]?.carrier_rate_lane_id) {
            //Had to create new currency for each because of passing by reference issues
            const amount = new Currency(response.data[0]?.total_pay);
            const carrier_rate = new Currency(response.data[0]?.total_pay);
            modelRow.set("amount", amount);
            modelRow.set("carrier_rate", carrier_rate);
            modelRow.set("carrier_rate_id", response.data[0]?.carrier_rate_id);
            modelRow.set("carrier_rate_lane_id", response.data[0]?.carrier_rate_lane_id);
        } else {
            modelRow.set("carrier_rate", null);
        }
    }

    setCarrierData(tableRow: TableRow, carrierData: any) {
        const modelRow = tableRow.data;
        modelRow.setValues({
            "payee_id": carrierData?.["payee.id"],
            "dot_number": carrierData?.["dot_number"],
            "icc_number": carrierData?.["icc_number"],
            "carrier_ranking": carrierData?.["carrier_ranking"],
            "carrier_name": carrierData?.["payee.name"],
            "contact_name": carrierData?.["contact.name"],
            "contact_phone": carrierData?.["calculated_phone"],
            "email": carrierData?.["contact.email"]
        });
        modelRow.setLookupModelData("contact_name", { "name": carrierData?.["contact.name"] });
        modelRow.setLookupModelData("carrier_name", { "name": carrierData?.["payee.name"] });
    }

    showTooltipOnDataDisplay(event: DataDisplayEvent) {
        this.offerUtil?.showTooltipOnDataDisplay(event);
    }

    /** This is an event handler for the onRowDisplay event of tableOffers.  */
    tableOffersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        this.offerUtil?.tableOffersOnRowDisplay(event);
        this.displayStatus(tableRow);
        if (tableRow.mode !== TableRowMode.QUICK_ADD) {
            const counterOfferAmount = tableRow.findComponentById("textboxCounterOffer") as Textbox;
            if (counterOfferAmount) {
                counterOfferAmount.addChangeListener((event: ChangeEvent) => this.showCounterOfferButtons(tableRow, event.newValue));
            }
        }
        const labelInvitedCarrier = tableRow.findComponentById("labelInvitedCarrier") as Label;
        if (labelInvitedCarrier) {
            const invitedCarrierDot = modelRow.get("invited_carrier.dot_number");
            const invitedCarrierIcc = modelRow.get("invited_carrier.icc_number");
            labelInvitedCarrier.visible = !StringUtil.isEmptyString(invitedCarrierDot) ||
                !StringUtil.isEmptyString(invitedCarrierIcc) || modelRow.getBoolean("is_invited_carrier");
            if (labelInvitedCarrier.visible) {
                this.setCarrierIdentificationNumbers(modelRow);
                this.configureInvitedCarrierTooltip(labelInvitedCarrier, modelRow);
                this.hideCarrierWarningsForInvitedCarrier(tableRow);
            }
        }
    }


    private setCarrierIdentificationNumbers(modelRow: ModelRow): void {
        if (StringUtil.isEmptyString(modelRow.get("dot_number")) &&
            StringUtil.isEmptyString(modelRow.get("icc_number"))) {

            const type = modelRow.get("number_type");
            const number = modelRow.get("number");

            if (StringUtil.equalsIgnoreCase(type, QuickOfferSlideout.DOT)) {
                modelRow.set("dot_number", number);
            } else if (StringUtil.equalsIgnoreCase(type, QuickOfferSlideout.MC_NUMBER)) {
                modelRow.set("icc_number", number);
            }
        }
    }

    private configureInvitedCarrierTooltip(label: Label, modelRow: ModelRow): void {
        CommonTooltips.setTooltipContentLayout(
            label,
            null,
            "lme/powerbroker/CarrierInvitedQuickInfo",
            {
                minWidth: 100,
                minHeight: 100
            },
            {
                dot_number: modelRow.get("dot_number"),
                icc_number: modelRow.get("icc_number")
            }
        );

        label.tooltipDynamicOptions = {position: Alignment.RIGHT};
    }
    buttonAssignCarrierOnClick(event: ClickEvent) {
        this.offerUtil.assignCarrierEvent(event);
    }

    displayStatus(tableRow: TableRow) {
        const status = (tableRow.data as ModelRow).get("status");
        const panelStatus = tableRow.findComponentById("panelStatus") as Panel;
        if (panelStatus) {
            this.offerUtil?.setOfferStatusLabel(status, panelStatus);
        }
        this.showCounterOfferButtons(tableRow);
    }

    private showCounterOfferButtons(tableRow: TableRow, counterOfferAmount?: Currency) {
        const status = (tableRow.data as ModelRow).get("status");
        counterOfferAmount = counterOfferAmount !== undefined ? counterOfferAmount : tableRow.data.getCurrency("counter_offer")?.amount;
        const isCounterOffer: boolean = (status == "O" || status == "F") && !StringUtil.isEmptyString(counterOfferAmount);
        const buttonCounterOffer = tableRow.findComponentById("buttonCounterOffer") as Button;
        if (buttonCounterOffer) {
            buttonCounterOffer.visible = isCounterOffer;
        }
        const buttonDeclineCounter = tableRow.findComponentById("buttonDeclineCounter") as Button;
        if (buttonDeclineCounter) {
            buttonDeclineCounter.visible = isCounterOffer && tableRow.data.getBoolean("electronic_offer");
        }
    }

    panelStatusOnClick(event: ClickEvent) {
        const panel = event.target as Panel;
        const row = TableRow.getContainingTableRow(panel);
        row.clicked();
        if (panel["statusDropDownItems"] != null)
            this.displayOfferStatusDropDowns(panel);
        else {
            this.populateAndDisplayOfferStatusDropDowns(panel);
        }
    }

    private displayOfferStatusDropDowns(panel: Panel) {
        const dropDownItems = panel["statusDropDownItems"];
        if (dropDownItems != null) {
            Overlay.showDropdown(panel, dropDownItems, null, { width: 225 }, {
                align: Alignment.LEFT,
                onClose: () => {
                    panel.borderShadow = false;
                }
            });
        }
    }

    private populateAndDisplayOfferStatusDropDowns(panel: Panel) {
        panel["statusDropDownItems"] = MovementOffers.OFFER_STATUS_OPTIONS.map(item => {
            const labelProps: Partial<LabelProps> = {
                text: item.displayValue,
                onClick: () => this.statusOnChange(item.value, panel)
            };
            return labelProps;
        })
        this.displayOfferStatusDropDowns(panel);
    }

    statusOnChange(value: string, panel: Panel) {
        const tableRow: TableRow = TableRow.getContainingTableRow(panel);
        const offerRow = tableRow.data;
        offerRow.set("status", value);
        this.offerUtil.setOfferStatusLabel(tableRow.data.get("status"), panel);
        this.showCounterOfferButtons(tableRow);
    }

    private getPrequalValidationQuickInfoLayout(message: string, vendorUrl: string) {
        const layout = Layout.getLayout("lme/powerbroker/PrequalValidationWarningQuickInfo");
        layout.addLayoutLoadListener(() => {
            layout["labelPrequalWarningMessage"].visible = true;
            layout["labelPrequalWarningMessage"].text = message;
            if (!StringUtil.isEmptyString(vendorUrl)) {
                layout["vendorSiteBtn"].visible = true;
                layout["vendorSiteBtn"].link = vendorUrl;
            } else {
                layout["vendorSiteBtn"].visible = false;
            }
        });
        return layout;
    }

    /** This is an event handler for the beforeRowSave event of tableCarrierOffer.  */
    tableCarrierOfferBeforeRowSave(event) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.isNull("source")) {
            modelRow.set("source", OfferSource.QUICKOFFER);
        }
        if (tableRow.data._appending)
            this.fillOfferReferenceData(tableRow.data, this.moveRow, this.orderID, this.moveRow.get("id"));
        const tbAmount = tableRow.findComponentById("textboxAmount");
        if (tbAmount != null) {
            if (tableRow.data.getCurrency("amount")?.getAmount() == tableRow.data.getCurrency("carrier_rate")?.getAmount()) {
                CommonTooltips.setTooltipContentLayout(
                    tbAmount,
                    tableRow.data.get("carrier_rate_lane_id"),
                    "lme/dispatch/rates/CarrierLaneRateQuickInfo"
                );
            } else {
                tbAmount.tooltip = null;
            }
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxContactName.  */
    textboxContactNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const contactName = event.target;
        const tableRow: TableRow = TableRow.getContainingTableRow(contactName);
        const modelRow: ModelRow = tableRow.data;
        event.filter.parent_row_id = modelRow.get("payee_id");
        event.filter.parent_row_type = "P";
        event.filter.is_active = "Y";

        if (event.filter.parent_row_id == null)
            event.preventDefault();
    }

    setCarrierDataFromPrequal(tableRow: TableRow, prequalData: any) {
        const modelRow = tableRow.data;
        modelRow.set("dot_number", prequalData?.["dot_number"]);
        modelRow.set("carrier_name", prequalData?.["company_name"]);
    }

async prequalifyCarrier(iccNumber: string, dotNumber: string, identifierType: string, tableRow: TableRow) {
    try {
        const type = this.determineIdentifierType(identifierType);
        const number = StringUtil.equalsIgnoreCase(type, "DOT") ? dotNumber : iccNumber;

        // Get prequalification data
        const prequalResponse = await Api.post("lme/powerbroker/prequal-carrier", {
            identifier_type: type,
            identifier_number: number
        });

        const prequalResultData = prequalResponse?.data?.[0];
        if (!prequalResultData) {
            return;
        }

        if(StringUtil.isEmptyString(prequalResultData?.dot_number)){
            prequalResultData.dot_number = dotNumber;
        }
        if(StringUtil.isEmptyString(prequalResultData?.mc_number)){
            prequalResultData.mc_number = iccNumber;
        }

        // Check if carrier is invited
        const invitedCarrierResult = await Api.post("lme/powerbroker/invited-carrier-details", {
            dot_number: prequalResultData?.dot_number,
            icc_number: prequalResultData?.mc_number,
        });

        const invitedCarrierData = invitedCarrierResult?.data?.[0];
        if (invitedCarrierData) {
            // set boolean to true so we know row is for invited carrier
            tableRow.data.set("is_invited_carrier", true);
            this.updateInvitedCarrierLabel(tableRow, invitedCarrierData,prequalResultData);
        } else {
            tableRow.data.set("is_invited_carrier", false);
            this.processPrequalResultData(prequalResultData, tableRow);
        }
    } catch (error) {
        log.error("Error during carrier prequalification:", error);
    }
}



    /** Centralized helper: hides warning/error images (and clears tooltips) when invited carrier label is shown */
    private hideCarrierWarningsForInvitedCarrier(tableRow: TableRow): void {
        const imageWarning = tableRow.findComponentById("imageWarning") as Image | Label;
        if (imageWarning) {
            imageWarning.visible = false;
            (imageWarning as any).tooltip = null;
            (imageWarning as any).tooltipDynamicContent = null;
        }
        const imageError = tableRow.findComponentById("imageError") as Label;
        if (imageError) {
            imageError.visible = false;
            (imageError as any).tooltip = null;
            (imageError as any).tooltipDynamicContent = null;
        }
    }

    private updateInvitedCarrierLabel(tableRow: TableRow, invitedCarrierData: any, prequalResultData: any): void {
        const labelInvitedCarrier = tableRow.findComponentById("labelInvitedCarrier") as Label;
        if (labelInvitedCarrier) {
            labelInvitedCarrier.visible = invitedCarrierData?.is_invited_carrier || false;
            const message = this.buildPrequalMessage(prequalResultData);
            const vendorUrl = prequalResultData?.vendor_url;
            labelInvitedCarrier.tooltipDynamicContent = () => {
                const layout = Layout.getLayout("lme/powerbroker/InvitedCarrierWithPrequalQuickInfo") as InvitedCarrierWithPrequalQuickInfo;
                layout.addLayoutLoadListener(() => {
                    layout.labelPrequalWarningMessage.visible = true;
                    layout.labelPrequalWarningMessage.text = message;
                    layout.textboxInvitedDate.text = invitedCarrierData?.invited_date;
                    layout.textboxInvitedBy.text = invitedCarrierData?.entered_by_user_id;
                    if (!StringUtil.isEmptyString(vendorUrl)) {
                        layout.vendorSiteBtn.visible = true;
                        layout.vendorSiteBtn.link = vendorUrl;
                    } else {
                        layout.vendorSiteBtn.visible = false;
                    }
                });
                return layout;
            };
            if (labelInvitedCarrier.visible) {
                this.hideCarrierWarningsForInvitedCarrier(tableRow);
            }
        }
    }

    private buildPrequalMessage(prequalResultData: any): string {
        if (prequalResultData?.highway_vendor) {
            return prequalResultData?.exist_in_vendor_system ?
                "Highway overall rules assessment: " + prequalResultData?.overall_result :
                "This carrier does not exist in Highway's system, \npre-qualification unavailable.";
        } else if (prequalResultData?.aa_vendor) {
            return prequalResultData?.exist_in_vendor_system ?
                "MyCarrierPortal risk assessment: " + prequalResultData?.risk_assessment :
                "This carrier does not exist in MyCarrierPortal's system, \npre-qualification unavailable.";
        } else if (prequalResultData?.rmis_vendor) {
            return prequalResultData?.exist_in_vendor_system ?
                "RMIS Carrier Qualification Status" :
                "This carrier does not exist in RMIS's system, \npre-qualification unavailable.";
        }
        return "Carrier pre-qualification information unavailable.";
    }

    private processPrequalResultData(prequalResultData: any, tableRow: TableRow): void {
        if (prequalResultData === null) {
            return;
        }

        const imageWarning = tableRow?.findComponentById("imageWarning") as Image;

        if (prequalResultData?.highway_vendor) {
            const existInSystem = prequalResultData?.exist_in_vendor_system;
            const overallResult = prequalResultData?.overall_result;
            if (!StringUtil.equalsIgnoreCase(overallResult, "Pass") || !existInSystem) {
                if (!existInSystem) {
                    const prequalErrorMessage = "This carrier does not exist in Highway's system, \npre-qualification unavailable.";
                    this.buildCarrierWarnings(imageWarning, prequalErrorMessage, null);
                } else {
                    const vendorUrl = prequalResultData?.vendor_url;
                    const prequalErrorMessage = "Highway overall rules assessment: " + overallResult;
                    this.buildCarrierWarnings(imageWarning, prequalErrorMessage, vendorUrl);
                }
            }
        } else if (prequalResultData?.aa_vendor) {
            const existInSystem = prequalResultData?.exist_in_vendor_system;
            const riskAssessment = prequalResultData?.risk_assessment;
            if (!StringUtil.equalsIgnoreCase(riskAssessment, "Acceptable") || !existInSystem) {
                if (!existInSystem) {
                    const prequalErrorMessage = "This carrier does not exist in MyCarrierPortal's system, \npre-qualification unavailable.";
                    this.buildCarrierWarnings(imageWarning, prequalErrorMessage, null);
                } else {
                    const vendorUrl = prequalResultData?.vendor_url;
                    const prequalErrorMessage = "MyCarrierPortal risk assessment: " + riskAssessment;
                    this.buildCarrierWarnings(imageWarning, prequalErrorMessage, vendorUrl);
                }
                this.setCarrierDataFromPrequal(tableRow, prequalResultData);
            }
        } else if (prequalResultData?.rmis_vendor) {
            const existInSystem = prequalResultData?.exist_in_vendor_system;
            const qualifiedCarrier = prequalResultData?.qualified_carrier;
            if (!qualifiedCarrier || !existInSystem) {
                if (!existInSystem) {
                    const prequalErrorMessage = "This carrier does not exist in RMIS's system, \npre-qualification unavailable.";
                    this.buildCarrierWarnings(imageWarning, prequalErrorMessage, null);
                } else {
                    const vendorUrl = prequalResultData?.vendor_url;
                    const rmisDotOk = prequalResultData?.dot_ok;
                    const rmisInsuranceOk = prequalResultData?.insurance_ok;
                    this.buildRmisCarrierWarnings(imageWarning, rmisDotOk, rmisInsuranceOk, vendorUrl);
                }
            }
        }
    }

    private buildRmisCarrierWarnings(imageWarning: Image, rmisDotOk: any, rmisInsuranceOk: any, vendorUrl: any) {
        if (imageWarning !== undefined && (!StringUtil.isEmptyString(rmisDotOk) || !StringUtil.isEmptyString(rmisInsuranceOk))) {
            imageWarning.visible = true;
            imageWarning.tooltipDynamicContent =
                () => this.getPrequalValidationQuickInfoRmisLayout(rmisDotOk, rmisInsuranceOk, vendorUrl);
        }
    }

    private getPrequalValidationQuickInfoRmisLayout(rmisDotOk: any, rmisInsuranceOk: any, vendorUrl: any) {
        const layout = Layout.getLayout("lme/powerbroker/PrequalValidationWarningQuickInfo");
        layout.addLayoutLoadListener(() => {
            if (!StringUtil.isEmptyString(rmisDotOk)) {
                layout["labelDotOk"].visible = true;
                layout["labelDotOk"].text = "RMIS DOT OK: " + rmisDotOk;
            }
            if (!StringUtil.isEmptyString(rmisInsuranceOk)) {
                layout["labelInsuranceOk"].visible = true;
                layout["labelInsuranceOk"].text = "Insurance OK: " + rmisInsuranceOk;
            }
            if (!StringUtil.isEmptyString(vendorUrl)) {
                layout["vendorSiteBtn"].visible = true;
                layout["vendorSiteBtn"].link = vendorUrl;
            } else {
                layout["vendorSiteBtn"].visible = false;
            }
        });
        return layout;
    }

    async validActiveVendor(): Promise<boolean> {
        const result = await this.sourceCarrierInsCtrl.search();
        if (result.modelRows[0] !== undefined) {
            return this.validateVendor(result.modelRows);
        }
        return false;
    }

    validateVendor(data: ModelRow<any>[]): boolean {
        return StringUtil.equalsIgnoreCase(data[0].get("vendor_id"), "H")
            || StringUtil.equalsIgnoreCase(data[0].get("vendor_id"), "D")
            || StringUtil.equalsIgnoreCase(data[0].get("vendor_id"), "R")
            || StringUtil.equalsIgnoreCase(data[0].get("vendor_id"), "C");
    }

    private buildCarrierWarnings(imageWarning: Image, prequalErrorMessage: string, vendorUrl: string) {
        if (imageWarning && !StringUtil.isEmptyString(prequalErrorMessage)) {
            imageWarning.visible = true;
            imageWarning.tooltipDynamicContent =
                () => this.getPrequalValidationQuickInfoLayout(prequalErrorMessage, vendorUrl);
        }
    }

    buttonDeclineCounterOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const offerId = tableRow.data.get("id");
        Api.post("lme/powerbroker/decline-counter-offer", { offer_id: offerId }).then(() => {
            tableRow.data.set("status", "D");
            this.displayStatus(tableRow);
        });
    }

    buttonCounterOfferOnClick(event: ClickEvent) {
        const buttonCounterOffer = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(buttonCounterOffer) as TableRow;
        const summaryInfo: CounterOfferSummaryInfo = {
            order_id: this.orderID,
            commodity: this.moveRow.get("orders.commodity_id"),
            distance: this.moveRow.get("move_distance"),
            weight: this.moveRow.get("orders.weight"),
            equipment_type: this.moveRow.get("orders.equipment_type_id")
        }
        CarrierOfferCounter.showCounterOfferSlideout({
            summaryInfo: summaryInfo,
            data: tableRow.data,
            doAfterSlideIn: (layout: CarrierOfferCounter) => {
                layout.setupCarrierInfo(this.activeRow.get("payee_id", null), this.moveRow.get("is_carrier_scorecard_licensed", false));
                layout.layoutMovementCarrierPay.displayData(this.moveRow, null, 0);
            },
            onSave: () => this.search()
        });
    }

    textboxContactNameOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        if (!textbox.isDropdownVisible()) {
            const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
            const modelRow: ModelRow = tableRow.data;
            if (modelRow.isNull("contact_name")) {
                modelRow.set("contact_phone", null);
                modelRow.set("email", null);
                tableRow.displayComponentValues();
            }
        }
    }

    textboxNumberTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const textboxIccNumber = tableRow.findComponentById("textboxIccNumber") as Textbox;
        if (textboxIccNumber) {
            textboxIccNumber.placeholder = event.selection?.displayValue;
        }
    }

    private determineIdentifierType(identifierType: string) {
        if(StringUtil.equalsIgnoreCase(identifierType, QuickOfferSlideout.DOT)){
            return "DOT";
        }else{
            return "MC";
        }
    }

    private setWarningLabels(tableRow: TableRow, visible: boolean) {
        const imageWarning = tableRow?.findComponentById("imageWarning") as Image;
        const imageError = tableRow?.findComponentById("imageError") as Label;
        if (imageWarning) {
            imageWarning.visible = visible;
        }
        if (imageError) {
            imageError.visible = false;
        }
        const labelInvitedCarrier = tableRow.findComponentById("labelInvitedCarrier") as Label;
        if (labelInvitedCarrier) {
            labelInvitedCarrier.visible = false;
        }

    }
}
